/**
 * Interactive Lookbook — premium two-column product presentation.
 *
 * Layout replicates the Didier Patisserie website product page:
 * - 50/50 flex split: photo left, info right
 * - Dark background (#3d3f4c), Titillium Web font, pink accent (#f8cde1)
 * - Contained photo with subtle margin & border-radius (not full-bleed)
 * - Thin chevron arrows on left/right edges, vertically centred
 * - Lookbook Header Banner asset (uploaded in Settings, falls back to dark + logo)
 *
 * Two-stage funnel: Presentation Set → Lookbook → Customer Wishlist → Customer PDF.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Fade,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Divider,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrintIcon from '@mui/icons-material/Print';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, loadOverridesMap, mergeOverrides } from '../shared/services/db';
import type { SavedLookbookRow } from '../shared/services/db';
import { API_BASE_URL } from '../shared/services/apiClient';
import { useLiveSettings } from '../hooks/useLiveSettings';
import { useAssetUrl } from '../hooks/useAssetUrl';
import { generateCatalogueHtml, toProperCase, cleanHandlingGuideline, type SupportedLang } from '../utils/generateCatalogueHtml';
import { localeField } from '../utils/localeField';
import i18n from '../i18n';
import { getAllergens, getSuitability, getBadgeColor } from '../utils/allergenHelpers';
import { boldAllergens } from '../utils/boldAllergens';
import type { ItemRow, AirlineTier } from '../shared/types';
import { AIRLINE_TIERS } from '../shared/types';
import { LOOKBOOK_CONTEXT_KEY, type LookbookContext } from './CataloguePage';
import { SYNC_CHANNEL_NAME, type SyncMessage } from '../shared/services/syncChannel';
import { useNavDrawer } from '../shared/contexts/NavDrawerContext';
import { useSync } from '../shared/contexts/SyncContext';
import { productPhotoUrl } from '../shared/services/photoCache';
import MenuIcon from '@mui/icons-material/Menu';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTour } from '../tour/TourProvider';

/* ── Exact Didier website palette ── */
const DK = '#363D4C';
const PINK = '#f8cde1';
const PHOTO_BG = '#ebebeb';
const FONT = "'Titillium Web', Arial, Helvetica, sans-serif";

function readLookbookContext(): LookbookContext | null {
  try {
    const raw = localStorage.getItem(LOOKBOOK_CONTEXT_KEY);
    if (raw) return JSON.parse(raw) as LookbookContext;
  } catch { /* ignore */ }
  return null;
}

export const LookbookPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openDrawer } = useNavDrawer();
  const { startTour } = useTour();
  const settings = useLiveSettings();
  const { photoCacheBust } = useSync();
  const fallbackLogoUrl = settings.logo_file
    ? `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(settings.logo_file)}`
    : null;
  const logoUrl = useAssetUrl('logo', fallbackLogoUrl);
  const lookbookBannerUrl = useAssetUrl('lookbook-banner', null);

  const [presentationItems, setPresentationItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [wishlist, setWishlist] = useState<Set<number>>(new Set());
  const [exportingPdf, setExportingPdf] = useState(false);

  // Portfolio drawer
  const [portfolioDrawerOpen, setPortfolioDrawerOpen] = useState(false);
  const [savedLookbooks, setSavedLookbooks] = useState<SavedLookbookRow[]>([]);
  const [portfolioName, setPortfolioName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Read context from localStorage
  const [ctx, setCtx] = useState<LookbookContext | null>(() => readLookbookContext());
  const showIngredients = ctx?.showIngredients ?? true;
  const airlineMode = ctx?.airlineMode ?? false;
  const showPrices = ctx?.showPrices ?? false;
  const showDefrostLife = ctx?.showDefrostLife ?? false;
  const showHandling = ctx?.showHandling ?? false;
  const tierMap = useMemo(
    () => new Map<number, AirlineTier>((ctx?.tierEntries ?? []) as [number, AirlineTier][]),
    [ctx?.tierEntries],
  );

  // Real-time sync via BroadcastChannel
  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(SYNC_CHANNEL_NAME);
      ch.onmessage = (event: MessageEvent<SyncMessage>) => {
        const msg = event.data;
        if (msg.type === 'SETTINGS_CHANGE') {
          setCtx(prev => prev ? {
            ...prev,
            airlineMode: msg.airlineMode,
            showIngredients: msg.showIngredients,
            showPrices: msg.showPrices,
            showDefrostLife: msg.showDefrostLife ?? false,
            showHandling: msg.showHandling ?? false,
            tierEntries: msg.tierEntries,
          } : prev);
        } else if (msg.type === 'SELECTION_CHANGE') {
          setCtx(prev => prev ? { ...prev, presentationSetIds: msg.selectedIds } : {
            presentationSetIds: msg.selectedIds,
            showIngredients: true,
            airlineMode: false,
            showPrices: false,
            tierEntries: [],
            timestamp: Date.now(),
          });
        } else if (msg.type === 'PRODUCT_UPDATE') {
          setPresentationItems(prev =>
            prev.map(item => item.itemid === msg.itemid ? { ...item, ...msg.patch } : item)
          );
        }
      };
    } catch { /* BroadcastChannel not supported */ }

    // Also listen for localStorage updates (initial tab open or older browser)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOOKBOOK_CONTEXT_KEY && e.newValue) {
        try { setCtx(JSON.parse(e.newValue) as LookbookContext); } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      ch?.close();
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Load items from IndexedDB when context changes
  useEffect(() => {
    const ids = ctx?.presentationSetIds ?? [];
    if (ids.length === 0) {
      setLoading(false);
      setPresentationItems([]);
      return;
    }
    setLoading(true);
    Promise.all([
      db.items.where('itemid').anyOf(ids).toArray(),
      loadOverridesMap(),
    ])
      .then(([rows, overrides]) => {
        const fp = rows.filter((r: ItemRow) => r.itemcategory === '5');
        // Merge local personalization so the lookbook, PDF and saved portfolio
        // snapshots reflect the user's edits (master stays untouched in Trac-IT).
        setPresentationItems(mergeOverrides(fp, overrides));
        setCurrentIndex(0);
        setLoading(false);
      })
      .catch(() => {
        setPresentationItems([]);
        setLoading(false);
      });
  }, [ctx?.presentationSetIds?.join(',')]);

  // Load saved lookbooks when drawer opens
  useEffect(() => {
    if (portfolioDrawerOpen) {
      db.savedLookbooks.toArray().then(rows => {
        setSavedLookbooks(rows.sort((a, b) => b.createdAt - a.createdAt));
      });
    }
  }, [portfolioDrawerOpen]);

  const handleSavePortfolio = useCallback(async () => {
    if (!ctx || !portfolioName.trim()) return;
    const row: SavedLookbookRow = {
      id: `lookbook-${Date.now()}`,
      name: portfolioName.trim(),
      createdAt: Date.now(),
      itemIds: ctx.presentationSetIds,
      items: presentationItems,   // embed full item snapshots for offline use
      showIngredients: ctx.showIngredients,
      airlineMode: ctx.airlineMode,
      showPrices: ctx.showPrices,
      showDefrostLife: ctx.showDefrostLife,
      showHandling: ctx.showHandling,
      tierEntries: ctx.tierEntries,
    };
    await db.savedLookbooks.put(row);
    setPortfolioName('');
    setSavedLookbooks(prev => [row, ...prev]);
  }, [ctx, portfolioName, presentationItems]);

  const handleLoadPortfolio = useCallback((row: SavedLookbookRow) => {
    const loaded: LookbookContext = {
      presentationSetIds: row.itemIds,
      showIngredients: row.showIngredients,
      airlineMode: row.airlineMode,
      showPrices: row.showPrices,
      showDefrostLife: row.showDefrostLife,
      showHandling: row.showHandling,
      tierEntries: row.tierEntries,
      timestamp: Date.now(),
    };
    localStorage.setItem(LOOKBOOK_CONTEXT_KEY, JSON.stringify(loaded));
    setCtx(loaded);
    // Use embedded items directly — no IndexedDB fetch needed (works offline)
    if (row.items && row.items.length > 0) {
      setPresentationItems(row.items);
      setCurrentIndex(0);
    }
    setPortfolioDrawerOpen(false);
  }, []);

  const handleDeletePortfolio = useCallback(async (id: string) => {
    await db.savedLookbooks.delete(id);
    setSavedLookbooks(prev => prev.filter(r => r.id !== id));
  }, []);

  const handleRenamePortfolio = useCallback(async (id: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    await db.savedLookbooks.where('id').equals(id).modify({ name: trimmed });
    setSavedLookbooks(prev => prev.map(r => r.id === id ? { ...r, name: trimmed } : r));
    setRenamingId(null);
  }, []);

  const currentItem = presentationItems[currentIndex] ?? null;
  const total = presentationItems.length;

  const toggleWishlist = useCallback((itemid: number) => {
    setWishlist(prev => {
      const next = new Set(prev);
      if (next.has(itemid)) next.delete(itemid);
      else next.add(itemid);
      return next;
    });
  }, []);

  const handleExportCustomerPdf = useCallback(async () => {
    if (wishlist.size === 0) return;
    const wishlistItems = presentationItems.filter(i => wishlist.has(i.itemid));
    if (wishlistItems.length === 0) return;
    setExportingPdf(true);
    try {
      let bannerDataUrl: string | null = null;
      const bannerRow = await db.assetBlobs.get('banner');
      if (bannerRow?.blob) {
        bannerDataUrl = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = () => reject(new Error('Failed to read banner'));
          r.readAsDataURL(bannerRow.blob);
        });
      }
      const lang = (i18n.language as SupportedLang) ?? 'en';
      const localisedWishlist = lang === 'en' ? wishlistItems : wishlistItems.map(item => ({
        ...item,
        itemdescription: localeField(item, 'itemdescription', lang) ?? item.itemdescription,
        item_ingredientlist: localeField(item, 'item_ingredientlist', lang) ?? item.item_ingredientlist,
        item_handling_guideline: localeField(item, 'item_handling_guideline', lang) ?? item.item_handling_guideline,
        item_marketingproducttitle: localeField(item, 'item_marketingproducttitle', lang) ?? item.item_marketingproducttitle,
        item_marketingdescription: localeField(item, 'item_marketingdescription', lang) ?? item.item_marketingdescription,
      }));
      const html = generateCatalogueHtml(
        localisedWishlist,
        settings,
        showPrices,
        airlineMode ? tierMap : undefined,
        showIngredients,
        bannerDataUrl,
        undefined,
        photoCacheBust,
        showDefrostLife,
        showHandling,
        lang,
      );
      const win = window.open('', '_blank');
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
      }
    } finally {
      setExportingPdf(false);
    }
  }, [wishlist, presentationItems, settings]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!currentItem) return;
      if (e.key === 'ArrowLeft') setCurrentIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIndex(i => Math.min(total - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentItem, total]);

  /* ── Loading ── */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: DK, fontFamily: FONT }}>
        <CircularProgress sx={{ color: PINK }} />
      </Box>
    );
  }

  /* ── Empty set ── */
  if (presentationItems.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: DK,
          color: 'rgba(255,255,255,0.9)',
          fontFamily: FONT,
          p: 4,
        }}
      >
        <Typography sx={{ fontWeight: 300, letterSpacing: 2, mb: 1, fontSize: '1.4rem' }}>
          {t('lookbook.no_products')}
        </Typography>
        <Typography sx={{ opacity: 0.7, mb: 3, fontSize: '0.95rem' }}>
          {t('lookbook.no_products_hint')}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            fontFamily: FONT,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: 2,
            color: PINK,
            borderColor: DK,
            border: `2px solid ${DK}`,
            borderRadius: '23px',
            px: 3,
            py: '11px',
            bgcolor: 'transparent',
            '&:hover': { color: DK, bgcolor: PINK, borderColor: PINK },
          }}
        >
          {t('lookbook.back_to_catalogue')}
        </Button>
      </Box>
    );
  }

  const photoUrl = currentItem?.itemcode
    ? productPhotoUrl(currentItem.itemcode, photoCacheBust)
    : null;
  const inWishlist = currentItem ? wishlist.has(currentItem.itemid) : false;
  const handlingText = cleanHandlingGuideline(
    currentItem
      ? (localeField(currentItem, 'item_handling_guideline', i18n.language) ?? currentItem.item_handling_guideline)
      : null
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: DK,
        overflow: 'hidden',
        fontFamily: FONT,
      }}
    >
      {/* ═══ HEADER: lookbook banner or dark + logo ═══ */}
      <Box sx={{ flexShrink: 0, bgcolor: DK }}>
        {lookbookBannerUrl ? (
          <Box
            component="img"
            src={lookbookBannerUrl}
            alt=""
            sx={{
              width: '100%',
              height: { xs: 56, sm: 80 },
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
            }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : null}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            px: 3,
            py: 1,
            gap: 2,
          }}
        >
          <IconButton onClick={openDrawer} size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            <MenuIcon />
          </IconButton>
          <Button
            size="small"
          onClick={() => navigate('/')}
          sx={{
            fontFamily: FONT,
            color: 'rgba(255,255,255,0.7)',
            fontSize: 13,
            letterSpacing: 1,
            '&:hover': { color: '#fff' },
          }}
        >
          ← {t('lookbook.back')}
        </Button>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt={settings.brand_name}
              sx={{
                height: 48,
                maxWidth: 360,
                width: 'auto',
                objectFit: 'contain',
                flexShrink: 0,
              }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          {(settings.brand_name || settings.brand_subtitle) && (
            <Box sx={{ minWidth: 0 }}>
              {settings.brand_name && (
                <Typography
                  noWrap
                  sx={{
                    fontFamily: FONT,
                    color: 'rgba(255,255,255,0.92)',
                    fontSize: 16,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    lineHeight: 1.2,
                  }}
                >
                  {settings.brand_name}
                </Typography>
              )}
              {settings.brand_subtitle && (
                <Typography
                  noWrap
                  sx={{
                    fontFamily: FONT,
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 11,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {settings.brand_subtitle}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontFamily: FONT,
              color: 'rgba(255,255,255,0.5)',
              fontSize: 13,
              letterSpacing: 2,
              fontWeight: 400,
            }}
          >
            {t('lookbook.counter', { current: currentIndex + 1, total })}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={t('tour.help.title')}>
              <IconButton
                onClick={() => startTour('lookbook')}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              data-tour="lb-portfolios"
              size="small"
              startIcon={<SaveIcon />}
              onClick={() => setPortfolioDrawerOpen(true)}
              sx={{
                fontFamily: FONT, fontSize: 13, fontWeight: 600, letterSpacing: 1,
                color: PINK, border: `1px solid ${PINK}`, borderRadius: '23px', px: 2, py: '6px',
                '&:hover': { color: DK, bgcolor: PINK },
              }}
            >
              {t('lookbook.portfolios')}
            </Button>
            {wishlist.size > 0 && (
              <Button
                data-tour="lb-pdf"
                size="small"
                startIcon={exportingPdf ? <CircularProgress size={14} color="inherit" /> : <PrintIcon />}
                onClick={handleExportCustomerPdf}
                disabled={exportingPdf}
                sx={{
                  fontFamily: FONT, fontSize: 14, fontWeight: 600, letterSpacing: 2,
                  color: PINK, bgcolor: DK, border: `2px solid ${PINK}`, borderRadius: '23px',
                  px: 2.5, py: '8px', '&:hover': { color: DK, bgcolor: PINK },
                }}
              >
                  {t('lookbook.customer_pdf', { count: wishlist.size })}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* ═══ MAIN: two-column 50/50 ═══ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: 0,
          position: 'relative',
        }}
      >
        {/* Prev arrow — left edge, vertically centred */}
        <IconButton
          onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
          disabled={currentIndex === 0}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            color: 'rgba(255,255,255,0.5)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 40, strokeWidth: 0.5 }} />
        </IconButton>

        {/* Next arrow — right edge, vertically centred */}
        <IconButton
          data-tour="lb-nav"
          onClick={() => setCurrentIndex(i => Math.min(total - 1, i + 1))}
          disabled={currentIndex >= total - 1}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            color: 'rgba(255,255,255,0.5)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 40, strokeWidth: 0.5 }} />
        </IconButton>

        <Fade in key={currentItem?.itemid} timeout={350}>
          <Box sx={{ display: 'flex', flex: 1, flexDirection: { xs: 'column', md: 'row' }, minHeight: 0 }}>
            {/* ── LEFT: Product photo panel (50%) ── */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: '20px',
                minHeight: { xs: 280, md: 0 },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: PHOTO_BG,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {photoUrl ? (
                  <Box
                    component="img"
                    src={photoUrl}
                    alt={currentItem?.itemdescription ?? ''}
                    sx={{
                      maxWidth: '92%',
                      maxHeight: '92%',
                      objectFit: 'contain',
                    }}
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <Typography sx={{ color: 'rgba(0,0,0,0.3)', letterSpacing: 2, fontFamily: FONT }}>
                    {t('lookbook.no_image')}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* ── RIGHT: Product info panel (50%) ── */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                display: 'flex',
                flexDirection: 'column',
                color: '#fff',
                overflow: 'hidden',
              }}
            >
              {/* Scrollable content area, vertically centered */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  px: { xs: 3, sm: '50px' },
                  py: { xs: 2, sm: '20px' },
                  overflow: 'auto',
                  minHeight: 0,
                }}
              >
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '1.8rem' },
                  lineHeight: 1.25,
                  color: '#fff',
                  mb: 1,
                }}
              >
                {(currentItem && localeField(currentItem, 'item_marketingproducttitle', i18n.language)) || currentItem?.item_marketingproducttitle || toProperCase(localeField(currentItem, 'itemdescription', i18n.language) ?? currentItem?.itemdescription ?? '')}
              </Typography>

              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 300,
                  fontSize: '0.85rem',
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: 1,
                  mb: 2.5,
                }}
              >
                {currentItem?.itemcode}
              </Typography>

              {currentItem?.item_marketingdescription && (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 400,
                    fontSize: '0.95rem',
                    color: 'rgba(255,255,255,0.85)',
                    lineHeight: 1.6,
                    mb: 2.5,
                  }}
                >
                  {localeField(currentItem, 'item_marketingdescription', i18n.language) ?? currentItem.item_marketingdescription}
                </Typography>
              )}

              {currentItem && (
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  {currentItem.pack_size != null && (
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                      {t('preview.case_size', { n: currentItem.pack_size })}
                    </Typography>
                  )}
                  {(() => {
                    const packSize = currentItem.pack_size != null && currentItem.pack_size > 0 ? currentItem.pack_size : null;
                    const totalWeight = currentItem.final_weight != null && currentItem.final_weight > 0 ? currentItem.final_weight : null;
                    const displayWeight = totalWeight != null && packSize != null ? totalWeight / packSize : totalWeight;
                    return displayWeight != null ? (
                      <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                        {packSize != null
                          ? t('preview.weight_per_unit', { n: displayWeight.toFixed(0) })
                          : t('preview.weight', { n: displayWeight.toFixed(0) })}
                      </Typography>
                    ) : null;
                  })()}
                  {(currentItem.category6_description || currentItem.category5_description) && (
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                      <strong style={{ color: 'rgba(255,255,255,0.95)' }}>{t('preview.fp_category')}</strong> {currentItem.category6_description ?? currentItem.category5_description}
                    </Typography>
                  )}
                </Stack>
              )}

              {airlineMode && currentItem && tierMap.has(currentItem.itemid) && (
                <Chip
                  label={AIRLINE_TIERS.find(t => t.value === tierMap.get(currentItem.itemid))?.label ?? tierMap.get(currentItem.itemid)}
                  size="small"
                  sx={{
                    bgcolor: PINK,
                    color: DK,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONT,
                    mb: 1.5,
                    alignSelf: 'flex-start',
                  }}
                />
              )}

              {currentItem && (
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                  {getSuitability(currentItem).map(s => (
                    <Chip
                      key={s}
                      label={t(`suitability.${s}`)}
                      size="small"
                      sx={{ bgcolor: getBadgeColor(s), color: '#fff', fontSize: 11, fontWeight: 500, fontFamily: FONT }}
                    />
                  ))}
                  {getAllergens(currentItem).map(a => (
                    <Chip
                      key={a}
                      label={t(`allergen.${a}`)}
                      size="small"
                      sx={{ bgcolor: getBadgeColor(a), color: '#fff', fontSize: 11, fontWeight: 500, fontFamily: FONT }}
                    />
                  ))}
                </Stack>
              )}

              {showIngredients && currentItem?.item_ingredientlist && (() => {
                const ingr = localeField(currentItem, 'item_ingredientlist', i18n.language) ?? currentItem.item_ingredientlist;
                return ingr ? (
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.7)',
                      lineHeight: 1.5,
                      mb: 2,
                      '& strong': { fontWeight: 700, color: 'rgba(255,255,255,0.9)' },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: `<strong style="color:rgba(255,255,255,0.9)">${t('pdf.ingredients')}</strong> ${boldAllergens(ingr)}`,
                    }}
                  />
                ) : null;
              })()}

              {showDefrostLife && currentItem?.int_life_2 != null && currentItem.int_life_2 > 0 && (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.9rem',
                    color: 'rgba(255,255,255,0.8)',
                    mb: 1.5,
                    '& strong': { color: 'rgba(255,255,255,0.95)' },
                  }}
                >
                  {t('preview.defrost_life', { n: Math.round(currentItem.int_life_2) })}
                </Typography>
              )}

              {showHandling && handlingText && (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '0.9rem',
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: 1.6,
                    mb: 2,
                    '& strong': { color: 'rgba(255,255,255,0.95)' },
                  }}
                >
                  <strong>{t('preview.handling')}</strong> {handlingText}
                </Typography>
              )}

              {showPrices && currentItem?.item_webprice != null && (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: PINK,
                    mb: 2,
                  }}
                >
                  £{Number(currentItem.item_webprice).toFixed(2)}
                </Typography>
              )}
              </Box>

              {/* Sticky action button at bottom of right panel */}
              <Box data-tour="lb-add" sx={{ flexShrink: 0, px: { xs: 3, sm: '50px' }, pb: { xs: 2, sm: '24px' }, pt: 1 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={inWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                  onClick={() => currentItem && toggleWishlist(currentItem.itemid)}
                  sx={{
                    fontFamily: FONT,
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: 2,
                    color: inWishlist ? DK : PINK,
                    bgcolor: inWishlist ? PINK : DK,
                    border: `2px solid ${inWishlist ? PINK : DK}`,
                    borderRadius: '23px',
                    px: 4,
                    py: '14px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    '&:hover': {
                      color: inWishlist ? PINK : DK,
                      bgcolor: inWishlist ? 'transparent' : PINK,
                      borderColor: inWishlist ? PINK : PINK,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {inWishlist ? t('lookbook.in_selection') : t('lookbook.add_to_selection')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>
      {/* ═══ PORTFOLIO DRAWER ═══ */}
      <Drawer
        anchor="right"
        open={portfolioDrawerOpen}
        onClose={() => setPortfolioDrawerOpen(false)}
        PaperProps={{ sx: { width: 360, bgcolor: DK, color: '#fff' } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '1.2rem', mb: 2 }}>
            {t('lookbook.my_portfolios')}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder={t('lookbook.portfolio_name_placeholder')}
              value={portfolioName}
              onChange={e => setPortfolioName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSavePortfolio(); }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': { color: '#fff', fontFamily: FONT },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: PINK },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSavePortfolio}
              disabled={!portfolioName.trim() || !ctx}
              sx={{
                bgcolor: PINK, color: DK, fontFamily: FONT, fontWeight: 600,
                '&:hover': { bgcolor: '#f0b8d0' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {t('preview.save')}
            </Button>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 1 }} />

          {savedLookbooks.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', mt: 2, textAlign: 'center' }}>
              {t('lookbook.no_portfolios')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {savedLookbooks.map(lb => (
                <Box key={lb.id} sx={{ mb: 0.5 }}>
                  {renamingId === lb.id ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1, py: 0.5 }}>
                      <TextField
                        size="small"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenamePortfolio(lb.id, renameValue);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        autoFocus
                        sx={{
                          flex: 1,
                          '& .MuiInputBase-root': { color: '#fff', fontFamily: FONT, fontSize: '0.85rem' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: PINK },
                        }}
                      />
                      <IconButton size="small" onClick={() => handleRenamePortfolio(lb.id, renameValue)}>
                        <CheckIcon sx={{ color: PINK, fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setRenamingId(null)}>
                        <CloseIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  ) : (
                    <ListItemButton
                      onClick={() => handleLoadPortfolio(lb)}
                      sx={{ borderRadius: 1, '&:hover': { bgcolor: 'rgba(248,205,225,0.1)' } }}
                    >
                      <FolderOpenIcon sx={{ color: PINK, mr: 1.5, fontSize: 20, flexShrink: 0 }} />
                      <ListItemText
                        primary={lb.name}
                        secondary={`${lb.itemIds.length} items · ${new Date(lb.createdAt).toLocaleDateString('en-GB')}`}
                        primaryTypographyProps={{ sx: { fontFamily: FONT, fontWeight: 500, color: '#fff' } }}
                        secondaryTypographyProps={{ sx: { fontFamily: FONT, fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' } }}
                      />
                      <ListItemSecondaryAction>
                        <Stack direction="row">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); setRenamingId(lb.id); setRenameValue(lb.name); }}>
                            <EditIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeletePortfolio(lb.id); }}>
                            <DeleteIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  )}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default LookbookPage;
